/**
 * Data Transmission Security Service
 * Implements TLS enforcement, certificate pinning, and additional encryption layers
 */

import CryptoJS from 'crypto-js';

class DataTransmissionSecurity {
  constructor() {
    this.minTLSVersion = '1.2';
    this.preferredTLSVersion = '1.3';
    this.certificatePins = new Map();
    this.initializeCertificatePins();
  }

  /**
   * Initialize certificate pins for known domains
   */
  initializeCertificatePins() {
    // Certificate pins for calyantechnology.com
    this.certificatePins.set('www.calyantechnology.com', [
      'sha256/AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=', // Replace with actual pin
      'sha256/BBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBB=' // Backup pin
    ]);
    
    // Add pins for other domains
    this.certificatePins.set('api.calyantechnology.com', [
      'sha256/CCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCC=',
      'sha256/DDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDD='
    ]);
  }

  /**
   * Validate TLS version for secure communication
   * @param {string} tlsVersion - TLS version string
   * @returns {boolean} True if TLS version is acceptable
   */
  validateTLSVersion(tlsVersion) {
    if (!tlsVersion) return false;
    
    const version = parseFloat(tlsVersion);
    return version >= parseFloat(this.minTLSVersion);
  }

  /**
   * Enforce minimum TLS version for all connections
   * @param {object} requestOptions - Request options
   * @returns {object} Updated request options with TLS enforcement
   */
  enforceTLSVersion(requestOptions = {}) {
    return {
      ...requestOptions,
      headers: {
        ...requestOptions.headers,
        'X-TLS-Version': this.preferredTLSVersion,
        'X-Require-TLS': this.minTLSVersion,
        'X-Force-TLS': 'true'
      }
    };
  }

  /**
   * Validate SSL certificate
   * @param {object} certificate - Certificate object
   * @returns {object} Validation result
   */
  validateCertificate(certificate) {
    const result = {
      isValid: false,
      errors: [],
      warnings: []
    };

    try {
      // Check if certificate exists
      if (!certificate) {
        result.errors.push('Certificate not provided');
        return result;
      }

      // Validate certificate format
      if (!certificate.subject || !certificate.issuer) {
        result.errors.push('Invalid certificate format');
        return result;
      }

      // Check certificate expiration
      const now = Date.now();
      if (certificate.validFrom && now < certificate.validFrom) {
        result.errors.push('Certificate not yet valid');
      }

      if (certificate.validTo && now > certificate.validTo) {
        result.errors.push('Certificate expired');
      }

      // Check certificate issuer
      if (certificate.issuer && !this.isTrustedIssuer(certificate.issuer)) {
        result.warnings.push('Certificate issuer not in trusted list');
      }

      // Validate certificate pin
      if (certificate.hostname && !this.validateCertificatePin(certificate.hostname, certificate.fingerprint)) {
        result.errors.push('Certificate pin validation failed');
      }

      result.isValid = result.errors.length === 0;
      return result;
    } catch (error) {
      result.errors.push('Certificate validation failed: ' + error.message);
      return result;
    }
  }

  /**
   * Check if certificate issuer is trusted
   * @param {string} issuer - Certificate issuer
   * @returns {boolean} True if issuer is trusted
   */
  isTrustedIssuer(issuer) {
    const trustedIssuers = [
      'DigiCert Inc',
      'Let\'s Encrypt',
      'GlobalSign',
      'Comodo',
      'GoDaddy',
      'Amazon',
      'Google',
      'Microsoft',
      'Cloudflare',
      'Sectigo'
    ];

    return trustedIssuers.some(trusted => issuer.includes(trusted));
  }

  /**
   * Validate certificate pin for hostname
   * @param {string} hostname - Hostname
   * @param {string} fingerprint - Certificate fingerprint
   * @returns {boolean} True if pin is valid
   */
  validateCertificatePin(hostname, fingerprint) {
    const pins = this.certificatePins.get(hostname);
    if (!pins) {
      // No pin configured for this hostname
      return true;
    }

    return pins.includes(fingerprint);
  }

  /**
   * Add additional encryption layer for sensitive data
   * @param {object} data - Data to encrypt
   * @param {string} sessionKey - Session-specific encryption key
   * @returns {string} Encrypted data
   */
  addEncryptionLayer(data, sessionKey) {
    try {
      const dataString = JSON.stringify(data);
      
      // First layer: AES-256 encryption with session key
      const encrypted = CryptoJS.AES.encrypt(dataString, sessionKey);
      
      // Second layer: Add integrity check
      const checksum = CryptoJS.SHA256(encrypted.toString()).toString();
      
      return JSON.stringify({
        encrypted: encrypted.toString(),
        checksum: checksum,
        timestamp: Date.now()
      });
    } catch (error) {
      throw new Error('Additional encryption layer failed: ' + error.message);
    }
  }

  /**
   * Remove additional encryption layer
   * @param {string} encryptedData - Encrypted data with layers
   * @param {string} sessionKey - Session-specific encryption key
   * @returns {object} Decrypted data
   */
  removeEncryptionLayer(encryptedData, sessionKey) {
    try {
      const dataObj = JSON.parse(encryptedData);
      
      // Validate checksum
      const calculatedChecksum = CryptoJS.SHA256(dataObj.encrypted).toString();
      if (calculatedChecksum !== dataObj.checksum) {
        throw new Error('Data integrity check failed');
      }
      
      // Decrypt first layer
      const decrypted = CryptoJS.AES.decrypt(dataObj.encrypted, sessionKey);
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedString) {
        throw new Error('Decryption failed');
      }
      
      return JSON.parse(decryptedString);
    } catch (error) {
      throw new Error('Remove encryption layer failed: ' + error.message);
    }
  }

  /**
   * Generate session-specific encryption key
   * @returns {string} Session key
   */
  generateSessionKey() {
    const keyLength = 32; // 256 bits
    const randomBytes = CryptoJS.lib.WordArray.random(keyLength);
    return CryptoJS.enc.Base64.stringify(randomBytes);
  }

  /**
   * Create secure request with TLS enforcement and certificate pinning
   * @param {string} url - Request URL
   * @param {object} options - Request options
   * @param {string} sessionKey - Optional session key for additional encryption
   * @returns {object} Secure request options
   */
  createSecureRequest(url, options = {}, sessionKey = null) {
    try {
      // Enforce TLS version
      let secureOptions = this.enforceTLSVersion(options);
      
      // Add certificate pinning headers
      const hostname = new URL(url).hostname;
      const pins = this.certificatePins.get(hostname);
      if (pins) {
        secureOptions.headers = {
          ...secureOptions.headers,
          'X-Certificate-Pin': pins.join(',')
        };
      }
      
      // Add additional encryption layer if session key provided
      if (sessionKey && options.body) {
        const encryptedBody = this.addEncryptionLayer(options.body, sessionKey);
        secureOptions.body = encryptedBody;
        secureOptions.headers['X-Encrypted-Body'] = 'true';
      }
      
      return secureOptions;
    } catch (error) {
      console.error('Failed to create secure request:', error);
      return options; // Fallback to original options
    }
  }

  /**
   * Validate secure response
   * @param {object} response - Response object
   * @param {string} sessionKey - Optional session key for decryption
   * @returns {object} Validated response
   */
  validateSecureResponse(response, sessionKey = null) {
    try {
      // Check TLS version
      const tlsVersion = response.headers.get('X-TLS-Version');
      if (!this.validateTLSVersion(tlsVersion)) {
        throw new Error('Insufficient TLS version');
      }
      
      // Validate certificate if available
      if (response.certificate) {
        const certValidation = this.validateCertificate(response.certificate);
        if (!certValidation.isValid) {
          throw new Error('Certificate validation failed: ' + certValidation.errors.join(', '));
        }
      }
      
      // Decrypt response body if encrypted
      if (response.headers.get('X-Encrypted-Body') === 'true' && sessionKey) {
        const decryptedBody = this.removeEncryptionLayer(response.body, sessionKey);
        response.body = decryptedBody;
      }
      
      return response;
    } catch (error) {
      console.error('Response validation failed:', error);
      throw error;
    }
  }

  /**
   * Get security configuration
   * @returns {object} Security configuration
   */
  getSecurityConfig() {
    return {
      minTLSVersion: this.minTLSVersion,
      preferredTLSVersion: this.preferredTLSVersion,
      certificatePins: Array.from(this.certificatePins.entries()),
      supportedAlgorithms: ['AES-256-CBC', 'AES-256-GCM', 'ChaCha20-Poly1305'],
      trustedIssuers: [
        'DigiCert Inc', 'Let\'s Encrypt', 'GlobalSign', 'Comodo',
        'GoDaddy', 'Amazon', 'Google', 'Microsoft', 'Cloudflare', 'Sectigo'
      ]
    };
  }

  /**
   * Test TLS connection to URL
   * @param {string} url - URL to test
   * @returns {Promise<object>} Test result
   */
  async testTLSConnection(url) {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        headers: {
          'X-Require-TLS': this.minTLSVersion
        }
      });
      
      const tlsVersion = response.headers.get('X-TLS-Version');
      const isValid = this.validateTLSVersion(tlsVersion);
      
      return {
        url,
        isValid,
        tlsVersion,
        status: response.status,
        certificate: response.certificate || null
      };
    } catch (error) {
      return {
        url,
        isValid: false,
        error: error.message
      };
    }
  }

  /**
   * Validate data integrity
   * @param {object} data - Data to validate
   * @param {string} checksum - Expected checksum
   * @returns {boolean} True if integrity check passes
   */
  validateDataIntegrity(data, checksum) {
    const dataString = typeof data === 'object' ? JSON.stringify(data) : data;
    const calculatedChecksum = CryptoJS.SHA256(dataString).toString();
    return calculatedChecksum === checksum;
  }

  /**
   * Generate checksum for data
   * @param {object|string} data - Data to generate checksum for
   * @returns {string} SHA-256 checksum
   */
  generateChecksum(data) {
    const dataString = typeof data === 'object' ? JSON.stringify(data) : data;
    return CryptoJS.SHA256(dataString).toString();
  }

  /**
   * Sanitize sensitive data for logging
   * @param {object|string} data - Data to sanitize
   * @returns {object|string} Sanitized data
   */
  sanitizeData(data) {
    if (typeof data === 'object') {
      const sanitized = { ...data };
      
      // Remove or mask sensitive fields
      if (sanitized.password) sanitized.password = '***';
      if (sanitized.token) sanitized.token = '***';
      if (sanitized.secret) sanitized.secret = '***';
      if (sanitized.key) sanitized.key = '***';
      
      // Remove medical records for HIPAA compliance
      if (sanitized.medicalRecord) delete sanitized.medicalRecord;
      if (sanitized.medicalData) delete sanitized.medicalData;
      if (sanitized.clinicalData) delete sanitized.clinicalData;
      
      // Mask email addresses
      if (sanitized.email) {
        const [username, domain] = sanitized.email.split('@');
        sanitized.email = `${username.slice(0, 2)}***@${domain}`;
      }
      
      // Mask phone numbers
      if (sanitized.phone) {
        sanitized.phone = sanitized.phone.replace(/(\d{3})\d{3}(\d{4})/, '$1***$2');
      }
      
      return sanitized;
    }
    
    return data;
  }

  /**
   * Log security event
   * @param {string} event - Event type
   * @param {object} data - Event data
   * @param {string} level - Log level
   */
  logSecurityEvent(event, data, level = 'info') {
    const sanitizedData = this.sanitizeData(data);
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      data: sanitizedData,
      level,
      checksum: this.generateChecksum(sanitizedData)
    };
    
    console.log(`[SECURITY-${level.toUpperCase()}]`, logEntry);
  }
}

// Create singleton instance
const dataTransmissionSecurity = new DataTransmissionSecurity();

export default dataTransmissionSecurity;